import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import NumericTextBoxViewModelType from '@/ViewModels/Controls/NumericTextBoxViewModel';
import LabelType from '@/Controls/Label';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import TypeConverter from '@/Utility/TypeConverter';
import ResourceManager from '@/Management/ResourceManager';

export default class NumericTextBox extends Control {

    private Label = ComponentManager.getControl<typeof LabelType>('Label');
    private jQueryControl: JQuery;
    private label: LabelType;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.NumericTextBoxExt>;

    public constructor(private viewModel: NumericTextBoxViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>').append('<input type="text">');

        const countDecimals = (this.viewModel.useUnlimitedDecimals && this.viewModel.displayValue) ? this.countDecimals(this.viewModel.displayValue()) : null;

        this.applyBindings(this.jQueryControl.find('input'), {
            kendoNumericTextBox: {
                value: this.viewModel.displayValue,

                format: this.defineComputed(() => {
                    if (countDecimals != null)
                        return `n${countDecimals}`;

                    return (this.viewModel.format == null || this.viewModel.format === '')
                        ? (`n${this.viewModel.floatingPrecision()}`)
                        : this.viewModel.format;
                }),

                decimals: countDecimals ?? this.viewModel.floatingPrecision,

                min: this.viewModel.min,

                max: this.viewModel.max,

                step: this.viewModel.step,

                round: true,

                culture: this.viewModel.culture,

                spinners: this.viewModel.showSpinners,

                widget: this.widget,

                spin: (e) => {
                    // Bei Betätigung des Spin-Buttons ändert sich nur der displayValue, daher müssen wir noch explizit
                    // das value des ViewModels aktualisieren.
                    const value = e.sender.value();
                    this.updateValue(value);
                },

                upArrowText: ResourceManager.getInstance().get('NUMERICTEXTBOX_SPINNER_UP'),
                downArrowText: ResourceManager.getInstance().get('NUMERICTEXTBOX_SPINNER_DOWN')
            },

            tooltip: this.viewModel.tooltip,

            focus: this.viewModel.isFocusable,

            hasFocus: this.viewModel.hasFocus
        });

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: kendo.ui.NumericTextBoxExt): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'numerictextbox',

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            visible: this.viewModel.isVisible,

            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];

                    if (this.viewModel.isCreatedFromTextBox()) {
                        styles.push('alignLeft');
                    }

                    return styles;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    let states = [];

                    if (this.viewModel.isRequired()) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid()) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    if (this.viewModel.isEnabled()) {
                        states.push('enabled');
                    } else {
                        states.push('disabled');
                    }

                    return states;
                }
            })
        });

        if (this.viewModel.maxLength >= 0) {
            this.jQueryControl.find('input').attr('maxlength', this.viewModel.maxLength);
        }

        this.applyBindings(this.jQueryControl.find('input.k-input.k-formatted-value'), {
            locator: this.defineComputed(() => {
                return this.viewModel.locator() + ".output";
            }),
            focus: this.viewModel.isFocusable
        });
        this.applyBindings(this.jQueryControl.find('input.k-input').not('.k-formatted-value'), {
            locator: this.viewModel.locator,
            attr: {
                readonly: this.defineComputed(() => {
                    return !this.viewModel.isEnabled();
                })
            }
        });

        this.applyBindings(this.jQueryControl.find('.k-numeric-wrap'), {
            invalid: this.viewModel.invalidationText
        });

        let arrows = this.jQueryControl.find('.k-select > .k-link');
        if (arrows.length === 2) {
            this.applyBindings(arrows.eq(0), {
                locator: this.defineComputed(() => {
                    return this.viewModel.locator() + '.button.increase';
                })
            });
            this.applyBindings(arrows.eq(1), {
                locator: this.defineComputed(() => {
                    return this.viewModel.locator() + '.button.decrease';
                })
            });
        }

        this.applyBindings(this.jQueryControl.find('input'), {
            tooltip: this.viewModel.tooltip,

            attr: {
                readonly: this.defineComputed(() => {
                    return !this.viewModel.isEnabled();
                })
            }
        });


        if (this.viewModel.label != null) {
            this.label = new this.Label(this.viewModel.label);
            let tuple = this.label.getTuple();

            tuple.control.appendTo(this.jQueryControl);
        }

        if (this.viewModel.onChange != null) {
            this.defineComputed(() => {
                this.viewModel.onChange(this.viewModel.value());
            });
        }

        if (this.viewModel.onKeyDown != null) {
            this.addEventHandler('keydown', this.jQueryControl, (event) => {
                this.viewModel.onKeyDown(event.keyCode);
            });
        }

        if (this.viewModel.onKeyUp != null) {
            this.addEventHandler('keyup', this.jQueryControl, (event) => {
                this.viewModel.onKeyUp(event.keyCode);
            });
        }

        // Bei einer Änderung des Wertes im input-Feld (displayValue) durch den Benutzer müssen wir den value des ViewModels aktualisieren.
        this.addEventHandler('input', this.jQueryControl, (event: JQuery.UIEventBase) => {
            let value = $(event.currentTarget).val();
            this.updateValue(value);
        }, 'input');

        if (this.viewModel.onSubmit != null) {
            this.addEventHandler('keypress', this.jQueryControl, (event: JQuery.KeyPressEvent) => {
                if (event.key === 'Enter') {
                    this.viewModel.onSubmit(this.viewModel.value());
                }
            });
        }
    }

    private updateValue(newVal) {
        const widget = this.widget();

        if (widget) {
            let val = widget._adjust(widget._parse(newVal));
            const isCurrency: boolean = this.viewModel.dotNetTypeName.indexOf('Currency') > -1;
            // Ist es ein Currency, dann ist es nullable! (da dies nicht möglich ist im Specifier einzustellen)
            const isNullable: boolean = isCurrency || TypeConverter.typeIsNullable(this.viewModel.dotNetTypeName);

            if ((this.viewModel.isRequired() || !isNullable) && val == null)
                val = 0;

            //Falls eine unbegrenzte Anzahl an Nachkommastellen eingegeben und angezeigt werden soll,
            //muss das Widget angepasst werden, um die Werte korrekt darzustellen.
            //Wenn es sich um eine ganze Zahl handelt, sollte die Logik nicht greifen.
            if (this.viewModel.useUnlimitedDecimals && this.viewModel.floatingPrecision() !== 0) {
                const countDecimals = this.countDecimals(val);
                // Wenn `decimals` nicht gesetzt ist, wird der Wert von `decimals` aus der Kendo-Culture übernommen,
                // was z. B. für 'de' nur 2 Nachkommastellen ist. Daher müssen wir hier `decimals` auf maximal 16 festlegen.
                // Der Benutzer kann beliebig viele Nachkommastellen eingeben, aber nach dem Verlassen des Eingabefeldes
                // wird Kendo die Zahl automatisch auf maximal 17 Stellen begrenzen,
                // da das `Number`-Format in JavaScript nicht mehr speichern kann.
                widget.setOptions({
                    decimals: 16, //Wie viele Nachkommastellen eingegeben werden dürfen.
                    format: `n${countDecimals}` //Wie der Wert im Feld dargestellt wird
                });

                val = NumericTypeHelper.toLocaleNumber(val, countDecimals);
            }
            else {
                // Das Runden von Kendo reicht hier nicht. Es muss auch der eigentlich Wert gerundet werden,
                // damit dieser serverseitig gespeichert werden kann
                val = NumericTypeHelper.toLocaleNumber(val, this.viewModel.floatingPrecision());
            }

            this.viewModel.value(val as any);
        }
    }

    private countDecimals(value: number | string): number {
        if (value == null) return 0;

        const parts = value.toString().split(".");

        if (parts.length < 2) return 0;

        return parts[1].length;
    }

    public addLabelReference(label: JQuery): JQuery {
        const id = this.identifier + '-input';
        this.getControl().find('input[data-role="numerictextbox"]').attr('id', id);
        label.attr('for', id);
        return label;
    }
}

ComponentManager.setControl('NumericTextBox', NumericTextBox, 'RADPresentation.HTMLClient.DefaultViewExtensions');